import request from "graphql-request";
import { GetStaticPaths, GetStaticPropsContext } from "next";
import Head from "next/head";
import { useTranslations } from "next-intl";

import { TextBlock } from "@shared/components";
import { urls } from "@shared/constants";
import { docTitle } from "@shared/utils";

import { TermsCallPage, TTermsCallPage } from "__generated__/types";

import * as constants from "src/constants";
import bundleMessages from "src/utils/bundleMessages";

type Props = {
  data: TTermsCallPage;
};

export default function TermsPage({ data }: Props) {
  const t = useTranslations(`${constants.MESSAGE_PREFIX}pages.Terms`);
  return (
    <>
      <Head>
        <title key="title">
          {docTitle(t("documentTitle"), data.callPage?.call.title.en)}
        </title>
        <meta
          property="og:title"
          content={docTitle(t("documentTitle"), data.callPage?.call.title.en)}
          key="og-title"
        />
        <meta
          property="og:url"
          content={`${urls.callPage(data.callPage?.slug ?? "")}/terms`}
          key="og-url"
        />
      </Head>
      <div className="flex flex-col items-center mx-auto py-10 lg:py-14">
        <div className="flex flex-col items-center gap-y-8 lg:gap-y-14">
          <TextBlock
            content={data.callPage?.call.termsAndConditions.en}
            containerClasses="md:w-[700px] max-w-screen-md mx-4"
          />
        </div>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths<{ id: string }> = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export async function getStaticProps({
  locale,
  params,
}: GetStaticPropsContext) {
  const messages = await bundleMessages(locale, [
    `${constants.MESSAGE_PREFIX}pages.Terms`,
  ]);

  const slug: string = params?.slug as string;

  const data: TTermsCallPage | null = await request(
    `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
    TermsCallPage,
    { slug },
  );

  if (!data?.callPage) {
    return {
      notFound: true,
      revalidate: 60,
    };
  }
  return {
    props: {
      data,
      messages,
    },
    revalidate: 60,
  };
}
